import React, { useState } from 'react';
import { TouchableOpacity, ActivityIndicator, Platform, Alert, StyleSheet, Image, Text, View } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useActionSheet } from '@expo/react-native-action-sheet';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import * as FileSystem from 'expo-file-system/legacy';
import { v4 as uuidv4 } from 'uuid';

import { supabase } from '../../services/supabase';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import theme from '../../config/theme';
import { extractPathFromUrl } from '../../utils/file/uploadImageHelper';
import { base64ToArrayBuffer } from '../../utils/file/base64Helpers';
import { useWebAlert, AlertButton } from '../WebAlert';
import { CampoMarcacaoBackgroundButtonLogger } from '../../utils/logger';
import { pickImageFromLibrary } from '@/utils/media/imagePicker';
import { assertStructuralOnline } from '@/services/checklist/ChecklistStructuralOfflineGate';
import { runTombstonePreflightForRemoteWrites } from '@/services/syncService';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getAndroidActionSheetSafeAreaStyle } from '@/utils/safeArea';

interface CampoTemplateWithBg {
  id: string;
  nome: string;
  tipo: string;
  backgroundUrl?: string | null;
}

export const CAMPO_MARCACAO_BACKGROUND_READ_ONLY = true;

const assertMarcacaoBackgroundNotRetired = (
  barriers: Map<string, Set<string>>,
  templateId: string,
  campoId: string
) => {
  const tables = APP_CONSTANTS.DATABASE.TABLES;
  if (
    barriers.get(tables.TEMPLATES_CHECKLIST.NAME)?.has(templateId) ||
    barriers.get(tables.GUINCHO_CAMPOS_TEMPLATE.NAME)?.has(campoId)
  ) {
    throw new Error('Imagem de marcação bloqueada por estrutura aposentada no preflight.');
  }
};

interface Props {
  campo: CampoTemplateWithBg;
  empresaId: string;
  templateId: string;
  onBackgroundUpdated: (campoId: string, url: string | null) => void;
}

const CampoMarcacaoBackgroundButton: React.FC<Props> = ({ campo, empresaId, templateId, onBackgroundUpdated }) => {
  const { showActionSheetWithOptions } = useActionSheet();
  const { showAlert } = useWebAlert();
  const [processing, setProcessing] = useState(false);
  const safeAreaInsets = useSafeAreaInsets();
  const imagemPresente = Boolean(campo.backgroundUrl);

  CampoMarcacaoBackgroundButtonLogger.Main.info(`[render] tipo=${campo.tipo} imagemPresente=${imagemPresente}`);

  // Helpers cross-platform
  const showMessage = (title: string, msg: string, buttons?: AlertButton[]) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, buttons ?? [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg, buttons ?? [{ text: 'OK' }]);
    }
  };

  const selectOrManage = () => {
    if (CAMPO_MARCACAO_BACKGROUND_READ_ONLY) {
      showMessage('Somente leitura', 'A imagem de fundo está temporariamente indisponível para alteração.');
      return;
    }

    const gate = assertStructuralOnline('field', 'edit');
    if (!gate.allowed) {
      showMessage(gate.title, gate.message);
      return;
    }

    CampoMarcacaoBackgroundButtonLogger.Main.info(`[selectOrManage] imagemPresente=${imagemPresente}`);
    const hasImage = imagemPresente;
    const options = hasImage ? ['Cancelar', 'Substituir imagem', 'Remover imagem'] : ['Cancelar', 'Selecionar imagem'];
    const cancelButtonIndex = 0;
    showActionSheetWithOptions({
      options,
      cancelButtonIndex,
      containerStyle: getAndroidActionSheetSafeAreaStyle({
        bottomInset: safeAreaInsets.bottom,
      }),
    }, async (buttonIndex) => {
      if (buttonIndex === 1) {
        await pickImage();
      } else if (hasImage && buttonIndex === 2) {
        await removeImage();
      }
    });
  };

  // Image picking
  const pickImage = async () => {
    CampoMarcacaoBackgroundButtonLogger.Main.info('[pickImage] iniciando');
    try {
      const outcome = await pickImageFromLibrary({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.8,
      }, {
        source: 'checklist_mark_background',
        screen: 'components/checklist/CampoMarcacaoBackgroundButton',
        action: 'pick_mark_background_image_from_library',
        entityType: 'template_mark_background',
        entityId: campo.id,
        templateId,
        empresaId,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar suas fotos.');
        return;
      }

      if (outcome.status === 'failed') {
        CampoMarcacaoBackgroundButtonLogger.Main.warn(
          `[pickImage] Falha ao abrir galeria: ${outcome.error.message}`
        );
        showMessage('Erro', 'Não foi possível selecionar a imagem.');
        return;
      }

      if (outcome.status !== 'success' || !outcome.result.assets?.length) {
        return;
      }

      const asset = outcome.result.assets[0];
      await handleSelectedImage(asset);
    } catch (err) {
      CampoMarcacaoBackgroundButtonLogger.Main.warn(`[pickImage] Exceção inesperada: ${err}`);
      showMessage('Erro', 'Não foi possível selecionar a imagem.');
    }
  };

  // Compress helpers
  /**
   * Compacta/redimensiona uma imagem no web seguindo o padrão usado em ChecklistPhotoField.
   * Retorna o payload base64 sem prefixo.
   */
  const compressImageWeb = async (
    uri: string,
    options: { maxWidth?: number; quality?: number } = {}
  ): Promise<string> => {
    const { maxWidth = 1280, quality = 0.75 } = options;

    console.info(`[compressImageWeb] Iniciando compressão para uri=${uri}`);
    const startTs = Date.now();

    return new Promise<string>((resolve, reject) => {
      const img: HTMLImageElement = document.createElement('img');
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        console.info('[compressImageWeb] Imagem carregada com sucesso');
        console.info(`[compressImageWeb] Dimensões originais: ${img.width}x${img.height}`);
        let { width, height } = img;
        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
          console.info(`[compressImageWeb] Redimensionando para: ${width}x${height}`);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas not supported'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        const duration = Date.now() - startTs;
        const tamanhoKb = (dataUrl.length / 1024).toFixed(1);
        CampoMarcacaoBackgroundButtonLogger.Main.info(`[compressImageWeb] compressão concluída em ${duration}ms, tamanho=${tamanhoKb}KB`);
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      };

      img.onerror = (err) => {
        CampoMarcacaoBackgroundButtonLogger.Main.info(`[compressImageWeb] Erro ao carregar/comprimir imagem: ${err}`);
        reject(new Error('Image load error'));
      };

      img.src = uri;
    });
  };

  const handleSelectedImage = async (asset: ImagePicker.ImagePickerAsset) => {
    CampoMarcacaoBackgroundButtonLogger.Main.info('[handleSelectedImage] asset recebido');
    setProcessing(true);
    try {
      let base64Data: string;
      if (Platform.OS === 'web') {
        // Obtém tamanho original (bytes) via fetch antes da compressão
        let originalSizeKB = 0;
        try {
          const origBlob = await fetch(asset.uri).then(r => r.blob());
          originalSizeKB = origBlob.size / 1024;
        } catch {}

        CampoMarcacaoBackgroundButtonLogger.Main.info(
          `[handleSelectedImage][WEB] Tamanho original: ${originalSizeKB.toFixed(1)} KB`
        );

        base64Data = await compressImageWeb(asset.uri, { maxWidth: 1280, quality: 0.75 });

        const compressedSizeKB = (base64Data.length * 0.75) / 1024; // chars -> bytes approx 3/4
        const reduction = originalSizeKB ? ((1 - compressedSizeKB / originalSizeKB) * 100).toFixed(0) : 'N/A';

        CampoMarcacaoBackgroundButtonLogger.Main.info(
          `[handleSelectedImage][WEB] Tamanho comprimido: ${compressedSizeKB.toFixed(1)} KB (redução ${reduction}%)`
        );
      } else {
        // Mobile: compactar/redimensionar imagem antes de salvar com logs detalhados
        CampoMarcacaoBackgroundButtonLogger.Main.info('[handleSelectedImage] Mobile: Iniciando compressão com expo-image-manipulator');

        let originalSizeKBMobile = 0;
        try {
          const info = await FileSystem.getInfoAsync(asset.uri);
          if (info.exists && info.size) originalSizeKBMobile = info.size / 1024;
        } catch {}

        CampoMarcacaoBackgroundButtonLogger.Main.info(
          `[handleSelectedImage] Mobile: Tamanho original ${originalSizeKBMobile.toFixed(1)} KB`
        );

        const manipulated = await (
          (ImageManipulator as any).manipulate ?? ImageManipulator.manipulateAsync
        )(
          asset.uri,
          [{ resize: { width: 800 } }],
          { compress: 0.75, format: ImageManipulator.SaveFormat.JPEG, base64: true }
        );

        const compressedSizeKBMobile = manipulated.base64
          ? (manipulated.base64.length * 0.75) / 1024
          : 0;
        const reductionMobile = originalSizeKBMobile
          ? ((1 - compressedSizeKBMobile / originalSizeKBMobile) * 100).toFixed(0)
          : 'N/A';

        CampoMarcacaoBackgroundButtonLogger.Main.info(
          `[handleSelectedImage] Mobile: Compressão concluída (${manipulated.width}x${manipulated.height}) – tamanho ${compressedSizeKBMobile.toFixed(1)} KB (redução ${reductionMobile}%)`
        );

        base64Data = manipulated.base64 as string;
      }

      // 1. Descobrir rowId (id da linha em guincho_marcacao_imagens)
      const existing = await databaseService.listItems<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME, { campo_id: campo.id });
      const now = Date.now();
      let rowId = existing[0]?.id ?? uuidv4();

      // 2. Nome do arquivo local e remoto padronizado: rowId.jpg
      const fileName = `${rowId}.jpg`;
      const storagePath = `${APP_CONSTANTS.STORAGE.FOLDER.CHECKLIST_MARCACAO_BKG}/${empresaId}/${fileName}`;
      const bucket = APP_CONSTANTS.STORAGE.BUCKET.CHECKLIST;

      const tombstoneBarriers = await runTombstonePreflightForRemoteWrites((message, ...args) => (
        CampoMarcacaoBackgroundButtonLogger.Main.info(message, ...args)
      ));
      assertMarcacaoBackgroundNotRetired(tombstoneBarriers, templateId, campo.id);

      // 3. Upload para o Supabase
      const fileData = Platform.OS === 'web' ? new Blob([base64ToArrayBuffer(base64Data)], { type: 'image/jpeg' }) : base64ToArrayBuffer(base64Data);
      const { error: uploadErr } = await supabase.storage.from(bucket).upload(storagePath, fileData, { upsert: true, contentType: 'image/jpeg' });
      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(storagePath);
      const publicURL = urlData?.publicUrl;
      if (!publicURL) throw new Error('Falha ao obter URL pública');

      CampoMarcacaoBackgroundButtonLogger.Main.info('[upload] URL publica obtida para imagem de fundo');

      // 4. Upsert em guincho_marcacao_imagens
      if (existing.length) {
        await databaseService.updateItem(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME, rowId, { foto_url: publicURL, updated_at: now });
      } else {
        await databaseService.createItem(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME, { id: rowId, empresa_id: empresaId, template_id: templateId, campo_id: campo.id, foto_url: publicURL, created_at: now, updated_at: now });
      }

      // 5. Salvar localmente para web / mobile (offline cache)
      if (Platform.OS === 'web') {
        const payload = {
          id: rowId,
          base64: base64Data,
          empresa_id: empresaId,
          template_id: templateId,
          campo_id: campo.id,
          foto_url: rowId,
          updated_at: now,
        };

        if (existing.length) {
          // Atualiza mantendo created_at original
          await databaseService.updateItem(
            APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
            rowId,
            payload,
            true
          );
        } else {
          await databaseService.createItem(
            APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
            { ...payload, created_at: now },
            true
          );
        }
      } else {
        const dirPath = `${FileSystem.documentDirectory}checklist-marcacao-bkg/${empresaId}/`;
        const fileUri = `${dirPath}${fileName}`;
        const dirInfo = await FileSystem.getInfoAsync(dirPath);
        if (!dirInfo.exists) await FileSystem.makeDirectoryAsync(dirPath, { intermediates: true });
        await FileSystem.writeAsStringAsync(fileUri, base64Data, { encoding: FileSystem.EncodingType.Base64 });

        // Verificação extra: garante que o arquivo existe e possui tamanho > 0
        const verify = await FileSystem.getInfoAsync(fileUri);
        if (!verify.exists) {
          throw new Error('Falha ao salvar arquivo localmente');
        }
        const savedSizeKB = verify.size ? (verify.size / 1024).toFixed(1) : 'N/A';
        CampoMarcacaoBackgroundButtonLogger.Main.info(`[handleSelectedImage] Mobile: Background salvo (${savedSizeKB} KB)`);
      }

      // Cache buster para forçar atualização do thumbnail sem afetar URL persistente
      const cacheBustedURL = `${publicURL}?v=${now}`;
      onBackgroundUpdated(campo.id, cacheBustedURL);
      CampoMarcacaoBackgroundButtonLogger.Main.info('[upload] onBackgroundUpdated chamado');
      showMessage('Sucesso', 'Imagem salva com sucesso!');
    } catch (err: any) {
      console.error(err);
      showMessage('Erro', err?.message || 'Falha ao salvar imagem.');
    } finally {
      setProcessing(false);
    }
  };

  const removeImage = async () => {
    CampoMarcacaoBackgroundButtonLogger.Main.info('[removeImage] iniciando');
    if (!campo.backgroundUrl) return;
    showMessage('Confirmar remoção', 'Deseja remover a imagem de fundo?', [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Remover', style: 'destructive', onPress: async () => {
          setProcessing(true);
          try {
            const tombstoneBarriers = await runTombstonePreflightForRemoteWrites((message, ...args) => (
              CampoMarcacaoBackgroundButtonLogger.Main.info(message, ...args)
            ));
            assertMarcacaoBackgroundNotRetired(tombstoneBarriers, templateId, campo.id);

            // Remove no storage
            const { bucket, path } = extractPathFromUrl(campo.backgroundUrl!);
            const { error: removeError } = await supabase.storage.from(bucket).remove([path]);
            if (removeError) {
              throw removeError;
            }

            // Remove registros locais
            const rows = await databaseService.listItems<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME, { campo_id: campo.id });
            if (rows.length) {
              await databaseService.deleteItem(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME, rows[0].id);
            }

            // Web: remove cache local
            if (Platform.OS === 'web') {
              await databaseService.deleteItem(APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME, rows[0]?.id, true);
            }

            // Mobile: remover arquivo físico se existir
            if (Platform.OS !== 'web' && rows[0]?.id) {
              try {
                const fileName = `${rows[0].campo_id}-`;
                const dirPath = `${FileSystem.documentDirectory}checklist-marcacao-bkg/${empresaId}/`;
                const dirInfo = await FileSystem.getInfoAsync(dirPath);
                if (dirInfo.exists) {
                  const files = await FileSystem.readDirectoryAsync(dirPath);
                  for (const file of files) {
                    if (file.startsWith(fileName)) {
                      const fileUri = dirPath + file;
                      await FileSystem.deleteAsync(fileUri, { idempotent: true });
                    }
                  }
                }
              } catch (e) {
                console.warn('Erro ao remover arquivo físico de background:', e);
              }
            }

            onBackgroundUpdated(campo.id, null);
            showMessage('Sucesso', 'Imagem removida com sucesso!');
          } catch (err: any) {
            showMessage('Erro', err?.message || 'Falha ao remover imagem.');
          } finally {
            setProcessing(false);
          }
        }
      }
    ]);
  };

  return (
    <View style={styles.readOnlyArea}>
      <TouchableOpacity
        style={styles.touchArea}
        onPress={selectOrManage}
        disabled={CAMPO_MARCACAO_BACKGROUND_READ_ONLY || processing}
        accessibilityLabel={imagemPresente ? 'Imagem de fundo, somente leitura' : 'Imagem de fundo indisponível'}
        accessibilityState={{ disabled: CAMPO_MARCACAO_BACKGROUND_READ_ONLY || processing }}
      >
        {processing ? (
          <ActivityIndicator size="small" color={theme.colors.primary.default} />
        ) : campo.backgroundUrl ? (
          <View style={styles.thumbnailWrapper}>
            <Image
              source={{ uri: campo.backgroundUrl }}
              style={styles.thumbnail}
              resizeMode="cover"
            />
            <MaterialCommunityIcons
              name="eye-circle"
              size={16}
              color={theme.colors.text.inverse}
              style={styles.editIconOverlay}
            />
          </View>
        ) : (
          <MaterialCommunityIcons name="image-plus" size={20} color={theme.colors.text.secondary} />
        )}
      </TouchableOpacity>
      <Text style={styles.readOnlyText}>
        {imagemPresente ? 'Visualização (somente leitura)' : 'Sem imagem (somente leitura)'}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  readOnlyArea: {
    alignItems: 'center',
  },
  touchArea: {
    marginLeft: 8,
    padding: 4,
  },
  thumbnailWrapper: {
    width: 32,
    height: 32,
    borderRadius: 4,
    overflow: 'visible',
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    borderRadius: 4,
  },
  editIconOverlay: {
    position: 'absolute',
    right: -4,
    bottom: -4,
    backgroundColor: 'rgba(0,0,0,0.4)',
    borderRadius: 8,
    padding: 1,
  },
  readOnlyText: {
    marginTop: 2,
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
  },
});

export default CampoMarcacaoBackgroundButton; 
